import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Mail, Plus, X } from 'lucide-react';
import { getPessoa, Pessoa } from '../api/pessoa';

interface DocEmail {
  id: number;
  numero?: number | null;
  serie?: number | null;
  destinatario_id?: number | null;
  tomador_id?: number | null;
}

interface Props {
  open: boolean;
  tipo: string; // 'nfe' | 'cte' | 'cte-os' | 'mdfe' — usado na URL da API
  tipoLabel: string;
  doc: DocEmail | null;
  emailEmitente?: string | null; // email_envio_notas do emitente (cópia)
  onClose: () => void;
  onEnviado?: (para: string[]) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EnviarEmailModal({
  open,
  tipo,
  tipoLabel,
  doc,
  emailEmitente,
  onClose,
  onEnviado
}: Props) {
  const [emails, setEmails] = useState<string[]>([]);
  const [novoEmail, setNovoEmail] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null);

  // Para CT-e o tomador é quem paga; destinatário como fallback. NF-e só tem destinatário.
  const pessoaId = doc ? (doc.tomador_id ?? doc.destinatario_id ?? null) : null;

  const pessoaQ = useQuery<Pessoa>({
    enabled: open && pessoaId != null,
    queryKey: ['pessoa', pessoaId],
    queryFn: () => getPessoa(pessoaId!)
  });

  // Pré-preenche com os e-mails do cadastro assim que o modal abre
  useEffect(() => {
    if (!open) return;
    const sugeridos = [pessoaQ.data?.email, emailEmitente]
      .map((e) => (e || '').trim())
      .filter((e) => EMAIL_RE.test(e));
    setEmails(Array.from(new Set(sugeridos)));
    setNovoEmail('');
    setMensagem('');
    setFeedback(null);
    setEnviando(false);
  }, [open, pessoaQ.data, emailEmitente]);

  if (!open || !doc) return null;

  function adicionarEmail() {
    const e = novoEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) {
      setFeedback({ tipo: 'err', msg: `"${novoEmail}" não é um e-mail válido.` });
      return;
    }
    setFeedback(null);
    if (!emails.includes(e)) setEmails((prev) => [...prev, e]);
    setNovoEmail('');
  }

  function removerEmail(e: string) {
    setEmails((prev) => prev.filter((x) => x !== e));
  }

  async function enviar(ev: React.FormEvent) {
    ev.preventDefault();
    // e-mail digitado mas ainda não adicionado conta também
    let lista = emails;
    const pendente = novoEmail.trim().toLowerCase();
    if (pendente) {
      if (!EMAIL_RE.test(pendente)) {
        setFeedback({ tipo: 'err', msg: `"${novoEmail}" não é um e-mail válido.` });
        return;
      }
      if (!lista.includes(pendente)) lista = [...lista, pendente];
    }
    if (lista.length === 0) {
      setFeedback({ tipo: 'err', msg: 'Adicione ao menos um e-mail.' });
      return;
    }
    setEnviando(true);
    setFeedback(null);
    try {
      const r = await fetch(`/api/${tipo}/${doc!.id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ para: lista, ...(mensagem.trim() ? { mensagem: mensagem.trim() } : {}) })
      });
      const data = await r.json();
      if (r.ok && data.sucesso) {
        setFeedback({ tipo: 'ok', msg: `E-mail enviado para ${(data.enviado_para ?? lista).join(', ')}.` });
        onEnviado?.(data.enviado_para ?? lista);
        setTimeout(onClose, 1800);
      } else {
        setFeedback({ tipo: 'err', msg: data.error || data.detail || 'Falha ao enviar o e-mail.' });
        setEnviando(false);
      }
    } catch (e) {
      setFeedback({ tipo: 'err', msg: 'Erro: ' + String(e) });
      setEnviando(false);
    }
  }

  const carregandoSugestao = pessoaId != null && pessoaQ.isLoading;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm dark:bg-black/70"
      onClick={enviando ? undefined : onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-sky-300 bg-white shadow-2xl dark:border-sky-500/30 dark:bg-ink-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg border border-sky-200 bg-sky-50 dark:border-sky-500/30 dark:bg-sky-500/10">
              <Mail className="h-4 w-4 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <h3 className="font-semibold leading-tight text-slate-950 dark:text-white">
                Enviar por e-mail
              </h3>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {tipoLabel} #{doc.numero}/{doc.serie ?? 1} · XML + PDF em anexo
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-ghost !h-9 !w-9 !px-2 !py-1"
            onClick={onClose}
            disabled={enviando}
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={enviar} className="flex-1 space-y-4 overflow-y-auto p-5">
          <div>
            <label className="field-label">Destinatários</label>
            {carregandoSugestao && (
              <div className="flex items-center gap-2 py-2 text-xs text-neutral-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando e-mail do cadastro...
              </div>
            )}
            {!carregandoSugestao && emails.length > 0 && (
              <ul className="mb-2 flex flex-wrap gap-1.5">
                {emails.map((e) => (
                  <li
                    key={e}
                    className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 py-1 pl-3 pr-1.5 text-xs font-medium text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300"
                  >
                    <span className="max-w-[16rem] truncate">{e}</span>
                    <button
                      type="button"
                      onClick={() => removerEmail(e)}
                      disabled={enviando}
                      className="grid h-4 w-4 place-items-center rounded-full hover:bg-sky-200 dark:hover:bg-sky-500/30"
                      aria-label={`Remover ${e}`}
                      title="Remover"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!carregandoSugestao && emails.length === 0 && (
              <p className="mb-2 text-xs text-amber-700 dark:text-amber-300">
                Nenhum e-mail no cadastro — informe abaixo.
              </p>
            )}
            <div className="flex gap-2">
              <input
                type="email"
                value={novoEmail}
                onChange={(e) => setNovoEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    adicionarEmail();
                  }
                }}
                placeholder="Adicionar e-mail..."
                className="field-input flex-1 !py-2 text-sm"
                disabled={enviando}
              />
              <button
                type="button"
                onClick={adicionarEmail}
                disabled={enviando || !novoEmail.trim()}
                className="btn-secondary !py-2"
                title="Adicionar e-mail à lista"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-xs text-neutral-500">
              Pré-preenchido com o e-mail do cadastro. Remova ou adicione antes de enviar.
            </p>
          </div>

          <div>
            <label className="field-label">Mensagem (opcional)</label>
            <textarea
              className="field-input"
              rows={3}
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              placeholder={`Segue ${tipoLabel} #${doc.numero} em anexo.`}
              disabled={enviando}
            />
          </div>

          {feedback && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                feedback.tipo === 'ok'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
                  : 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300'
              }`}
            >
              {feedback.msg}
            </div>
          )}

          <div className="-mx-5 -mb-5 flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-white/10 dark:bg-slate-950/70">
            <button type="button" onClick={onClose} disabled={enviando} className="btn-secondary">
              Cancelar
            </button>
            <button type="submit" disabled={enviando || (emails.length === 0 && !novoEmail.trim())} className="btn-sky">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {enviando ? 'Enviando...' : 'Enviar e-mail'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
